import { useMemo } from "react";
import { useQuery } from "@stadium/services/apollo-client";

import useFetchAccountShops from "@hooks/stores/use-fetch-account-shops";
import { fetchStoresCategories } from "@graphql/queries/stores/fetch-categories";

import { mergeArraysAtSpecificIdx } from "@stadium/helpers/array-methods";
import { getFormattedSlug, isMasterCompany } from "@helpers/templates-listing";

import { CUSTOMER_CASE_STUDY_SLUG } from "@constants/templates-listing";

const ACCOUNT_COMMON_VARIABLES = {
  type: "account",
};

const emptyArr = [];
const emptyObj = {};

const useFetchMarketingCategories = ({
  currentCategory,
  templateSlug = emptyArr,
  includeRestrictions = emptyArr,
  showAccountShops = true,
}) => {
  const CATEGORIES_QUERY_VARIABLES = {
    variables: {
      onlyEnabled: true,
      atleastOneLiveStore: true,
      includeRestrictions: includeRestrictions,
    },
    notifyOnNetworkStatusChange: true,
  };

  const { data, loading: loadingMarketingCategories } = useQuery(
    fetchStoresCategories,
    CATEGORIES_QUERY_VARIABLES,
  );
  const { marketingCategories = emptyArr } = data || emptyObj;

  const {
    mappedAccountShops,
    formattedAccounts,
    loading: loadingAccountShops,
  } = useFetchAccountShops({
    queryVariableType: ACCOUNT_COMMON_VARIABLES,
    showAccountShops,
  });

  const modifiedAccountsForSlug = useMemo(
    () =>
      formattedAccounts?.map((account) => ({ ...account, slug: getFormattedSlug(account?.name) })),
    [formattedAccounts],
  );

  const modifiedMarketingCategories = useMemo(
    () => mergeArraysAtSpecificIdx(marketingCategories, modifiedAccountsForSlug, 1),
    [marketingCategories, modifiedAccountsForSlug],
  );

  const marketingCategoriesWithoutCaseStudy = useMemo(
    () => modifiedMarketingCategories.filter((item) => item.slug !== CUSTOMER_CASE_STUDY_SLUG),
    [modifiedMarketingCategories],
  );

  const initialCategoryPayloadWithName = useMemo(
    () =>
      currentCategory &&
      modifiedMarketingCategories?.find((category) => category.name === currentCategory),
    [modifiedMarketingCategories, currentCategory],
  );

  const initialCategoryPayloadWithSlug = useMemo(
    () =>
      templateSlug &&
      modifiedMarketingCategories?.find((category) => category.slug === templateSlug[0]),
    [templateSlug, modifiedMarketingCategories],
  );

  const initialCategoryPayload =
    templateSlug.length > 0 ? initialCategoryPayloadWithSlug : initialCategoryPayloadWithName;

  const { id: initialCategoryId, __typename: categoryTypeName } =
    initialCategoryPayload || emptyObj;

  const accountShops = isMasterCompany(categoryTypeName)
    ? mappedAccountShops[initialCategoryId]
    : null;

  return {
    loadingMarketingCategories,
    marketingCategories,
    mappedAccountShops,
    loadingAccountShops,
    modifiedMarketingCategories,
    initialCategoryPayload,
    accountShops,
    modifiedAccountsForSlug,
    marketingCategoriesWithoutCaseStudy,
  };
};

export default useFetchMarketingCategories;
